import { useEffect, useRef, useState } from "preact/hooks";
import { render } from "preact";
import type { ModalConfig } from "../shared/types.js";
import { ModalForm } from "./modal-form.js";
import css from "../styles/tailwind.css?inline";

let activeHost: HTMLElement | null = null;
let activeTrigger: HTMLElement | null = null;
let savedOverflow = "";

function ModalContent({ config, onClose }: { config: ModalConfig; onClose: () => void }) {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));

    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  function handleBackdropClick(e: MouseEvent) {
    if (e.target === backdropRef.current) onClose();
  }

  return (
    <div
      ref={backdropRef}
      data-backdrop
      class={`fixed inset-0 z-[999999] flex items-center justify-center transition-opacity duration-200 ease-out ${visible ? "opacity-100" : "opacity-0"}`}
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={handleBackdropClick}
    >
      <div
        ref={containerRef}
        class={`relative overflow-auto bg-theme-bg font-theme transition-transform duration-200 ease-out ${visible ? "scale-100" : "scale-95"} w-[min(520px,90vw)]`}
        style={{
          "--fv-button-bg": config.trigger.colors.button,
          "--fv-button-text": config.trigger.colors.buttonText,
          borderRadius: "var(--fv-theme-radius, 16px)",
          minHeight: "300px",
          maxHeight: "90vh",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 24px 60px -20px rgba(15,23,42,0.25), 0 8px 24px -12px rgba(15,23,42,0.15)",
        } as Record<string, string>}
      >
        <button
          type="button"
          class="absolute top-2 right-2 z-10 w-8 h-8 rounded-lg border-0 bg-transparent cursor-pointer text-theme-muted-fg hover:bg-[#f3f4f6] hover:text-[#475569] inline-flex items-center justify-center"
          aria-label="Close"
          onClick={onClose}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </button>

        <ModalForm config={config} containerRef={containerRef} />
      </div>
    </div>
  );
}

export function openModal(config: ModalConfig, triggerEl?: HTMLElement): void {
  if (activeHost) return;

  const host = document.createElement("div");
  document.body.appendChild(host);
  activeHost = host;
  activeTrigger = triggerEl ?? null;

  const shadow = host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = css;
  shadow.appendChild(style);

  const root = document.createElement("div");
  shadow.appendChild(root);

  savedOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  render(<ModalContent config={config} onClose={() => closeModal()} />, root);
}

export function closeModal(triggerEl?: HTMLElement): void {
  if (!activeHost) return;
  if (triggerEl && activeTrigger && triggerEl !== activeTrigger) return;

  document.body.style.overflow = savedOverflow;

  const host = activeHost;

  const shadow = host.shadowRoot;
  const backdrop = shadow?.querySelector("[data-backdrop]") as HTMLElement | null;
  const cleanup = () => {
    if (host.parentNode) host.remove();
    if (activeHost === host) { activeHost = null; activeTrigger = null; }
  };
  if (backdrop) {
    backdrop.classList.remove("opacity-100");
    backdrop.classList.add("opacity-0");
    backdrop.addEventListener("transitionend", cleanup, { once: true });
    setTimeout(cleanup, 300);
  } else {
    cleanup();
    activeHost = null;
  }
}
